import { Layers3, Ruler, Tags } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import type {
  FieldPilotLayerId,
  FieldPilotLayerVisibility,
} from "@/features/field-pilot/use-field-pilot-layers";

export interface FieldPilotLayerDefinition {
  id: FieldPilotLayerId;
  label: string;
  icon: LucideIcon;
  accessibleSwitchLabel: string;
}

export const FIELD_PILOT_LAYER_DEFINITIONS: FieldPilotLayerDefinition[] = [
  {
    id: "measurements",
    label: "Measurements",
    icon: Ruler,
    accessibleSwitchLabel: "Show measurements layer",
  },
  {
    id: "tags",
    label: "Tags",
    icon: Tags,
    accessibleSwitchLabel: "Show tag layer",
  },
];

export interface FieldPilotLayersMenuProps {
  visibility: FieldPilotLayerVisibility;
  onVisibilityChange: (layerId: FieldPilotLayerId, visible: boolean) => void;
  counts: Partial<Record<FieldPilotLayerId, number>>;
}

export function FieldPilotLayersMenu({
  visibility,
  onVisibilityChange,
  counts,
}: FieldPilotLayersMenuProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <Layers3 className="size-4" aria-hidden="true" />
          Layers
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Layers
          </p>
          <ul className="space-y-2">
            {FIELD_PILOT_LAYER_DEFINITIONS.map((layer) => {
              const Icon = layer.icon;
              const count = counts[layer.id];
              const checked = visibility[layer.id];
              const switchId = `field-pilot-layer-${layer.id}`;
              return (
                <li
                  key={layer.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-2 py-1.5"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0">
                      <Label htmlFor={switchId} className="text-sm font-medium">
                        {layer.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {count === undefined ? "—" : `${count} on page`}
                      </p>
                    </div>
                  </div>
                  <div className="flex h-11 w-11 items-center justify-center">
                    <Switch
                      id={switchId}
                      checked={checked}
                      onCheckedChange={(next) => onVisibilityChange(layer.id, next)}
                      aria-label={layer.accessibleSwitchLabel}
                      className="focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
